import { useEffect, useState } from 'react';
import { localeUI } from '../lib/format';
import { AlarmClock, AlertTriangle, CalendarDays, Check, ClipboardList, Tag, Timer, X } from 'lucide-react';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { PropertyRoofView } from './PropertyRoofView';
import { RChip } from './ui/RChip';
import { followUpState, followUpLabel, FOLLOWUP_COLOR } from '../lib/followUpColor';

/** Fila mínima que consumen las secciones de Welcome Call / Ready to File. */
export type FlowRow = {
  presence_id: string; claim_id: string; status: string | null; status_since: string;
  /** Antigüedad REAL desde el board (mig 0372). Null si el board no cubre ese claim.
   *  NO reemplaza a `status_since`, que está backfilleada en 572 de 1405 filas. */
  waiting_approval_on?: string | null;
  homeowner_name: string | null; claim_number: string | null; address: string | null;
  contractor_name: string | null; insurance_company: string | null; claim_type?: string | null; date_of_loss?: string | null;
  assigned_person?: string | null; assigned_team_name?: string | null; is_existing_claim?: boolean;
  intake_status?: 'complete' | 'incomplete' | 'missing'; has_photos?: boolean; photos_url?: string | null;
  contract_signed?: boolean; contract_sign_method?: string | null; has_policy?: boolean;
  welcome_call_done?: boolean; wc_result?: string | null; wc_problem?: boolean; wc_has_recording?: boolean;
  submission_token?: string | null; photo_url?: string | null;
  next_follow_up_at?: string | null; next_follow_up_calc?: string | null; last_follow_up_at?: string | null;
  /** Quién hizo el último follow-up (nombre + id para el avatar del panel). */
  last_follow_up_by?: string | null; last_follow_up_by_id?: string | null;
  scheduled_start?: string | null; scheduled_inspector?: string | null;
};

/** Foto de la casa (street view / companycam) para mostrar en las tarjetas colapsadas. */
export function RowHousePhoto({ url }: { url?: string | null }) {
  if (!url) return null;
  return (
    <div style={{ margin: '-12px -12px 10px', borderRadius: '12px 12px 0 0', overflow: 'hidden', maxHeight: 150 }}>
      <img src={url} alt="" loading="lazy" style={{ display: 'block', width: '100%', height: 150, objectFit: 'cover' }} />
    </div>
  );
}

export const daysAt = (iso: string | null): number | null =>
  iso ? Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86400000)) : null;

const B: React.CSSProperties = { fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)' as React.CSSProperties['fontWeight'] };
/** Fecha corta del follow-up ("—" si no hay). */
const fmtFu = (iso?: string | null): string => (iso ? new Date(iso).toLocaleDateString(localeUI()) : '—');
/** Chip de readiness compartido — ahora sobre RChip (rediseño Tech Premium). */
export function Badge({ ok, warn, label }: { ok?: boolean; warn?: boolean; label: string }) {
  return (
    <RChip variant={ok ? 'ok' : warn ? 'warn' : 'no'} icon={ok ? Check : warn ? undefined : X}>
      {!ok && warn ? `… ${label}` : label}
    </RChip>
  );
}

/** Tira de badges de estado del claim (Welcome Call / Intake / Photos / Contract / Policy). */
export function FlowBadges({ r, showWc = true, showDays = true, showFollowUp = true }: { r: FlowRow; showWc?: boolean; showDays?: boolean; showFollowUp?: boolean }) {
  const { t } = useI18n();
  const d = daysAt(r.status_since);
  const intakeOk = r.intake_status === 'complete';
  const intakeWarn = r.intake_status === 'incomplete';
  return (
    <span style={{ display: 'inline-flex', gap: 5, flexWrap: 'wrap', alignItems: 'center' }}>
      {showWc && <Badge ok={r.welcome_call_done} warn={!r.welcome_call_done && r.wc_has_recording} label={t('flow.wc')} />}
      <Badge ok={intakeOk} warn={intakeWarn} label={t('flow.intake')} />
      <Badge ok={!!r.has_photos} label={t('flow.photos')} />
      <Badge ok={!!r.contract_signed} label={t('flow.contract')} />
      {r.claim_type && <span className="badge gray" style={B}>{r.claim_type}</span>}
      {r.wc_problem && <RChip variant="no" icon={AlertTriangle}>{t('flow.problem')}</RChip>}
      {/* Días en la ETAPA (no es "sin follow-up": para eso están los chips de fecha de abajo). */}
      {showDays && d != null && <RChip variant={d >= 7 ? 'no' : 'info'} icon={Timer} title={t('fu.daysInStage')}>{d}d</RChip>}
      {/* Follow-up del depto, visible sin desplegar: último + próximo con su fecha y semáforo. */}
      {showFollowUp && <span className="badge gray" style={B} title={t('fu.lastFu')}><CalendarDays size={12} strokeWidth={1.75} /> {fmtFu(r.last_follow_up_at)}</span>}
      {showFollowUp && r.next_follow_up_at && (
        <span className="badge" title={t('fu.next')}
          style={{ ...B, background: FOLLOWUP_COLOR[followUpState(r.next_follow_up_at)].bg,
            color: FOLLOWUP_COLOR[followUpState(r.next_follow_up_at)].fg,
            border: `1px solid ${FOLLOWUP_COLOR[followUpState(r.next_follow_up_at)].border}` }}>
          <AlarmClock size={12} strokeWidth={1.75} /> {fmtFu(r.next_follow_up_at)} · {followUpLabel(r.next_follow_up_at)}
        </span>
      )}
    </span>
  );
}

type Summary = {
  homeowner_name: string | null; homeowner_email: string | null; phone: string | null; address: string | null;
  contractor_name: string | null; insurance_company: string | null; claim_type: string | null; type_of_loss: string | null;
  date_of_loss: string | null; preferred_language: string | null; property_id: string | null;
  street_view_url: string | null; roof_squares: number | null; roof_pitch_deg: number | null;
  claim_number?: string | null; policy_number?: string | null; intake_form_url?: string | null;
  policy_summary?: { policy_type?: string; deductible?: string; limits?: string; building_code?: string; clauses?: string[] } | null;
};

/** Cabecera de info del claim: foto de la casa + datos clave + resumen de póliza.
 *  `hideName`: omite el nombre (cuando ya se muestra en la cabecera de la fila). */
export function ClaimInfoHeader({ claimId, hideName = false }: { claimId: string; hideName?: boolean }) {
  const { t } = useI18n();
  const [s, setS] = useState<Summary | null>(null);
  useEffect(() => { api.get<Summary>(`/api/claims/${claimId}/summary`).then(setS).catch(() => {}); }, [claimId]);
  const row: React.CSSProperties = { fontSize: 'var(--fs-xs)' };
  return (
    <div className="card" style={{ display: 'grid', gap: 10 }}>
      <div style={{ display: 'grid', gap: 3 }}>
        {!hideName && <div style={{ fontWeight: 'var(--fw-bold)' as React.CSSProperties['fontWeight'], fontSize: 'var(--fs-base)' }}>{s?.homeowner_name || '—'}</div>}
        {/* Arriba y destacado: tipo de daño (claim type) + fecha de pérdida. */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 2 }}>
          {(s?.claim_type || s?.type_of_loss) && <span className="badge" style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)' as React.CSSProperties['fontWeight'], background: 'var(--accent-soft)', color: 'var(--accent)' }}><Tag size={12} strokeWidth={1.75} /> {s?.claim_type || s?.type_of_loss}</span>}
          <span className="badge" style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)' as React.CSSProperties['fontWeight'], background: 'var(--bg)', color: 'var(--ink)' }}><CalendarDays size={12} strokeWidth={1.75} /> {t('cdw.dateOfLoss')}: {s?.date_of_loss || '—'}</span>
        </div>
        <div style={row}><span className="muted">{t('cci.address')}: </span>{s?.address || '—'}</div>
        {/* Claim # + Policy # a la vista (Scheduling filea el claim con estos dos). */}
        {/* Claim # y Policy # en Geist Mono (principio 5 del rediseño: IDs siempre en mono). */}
        <div style={row}><span className="muted">Claim #: </span><b style={{ fontFamily: 'var(--font-mono)' }}>{s?.claim_number || '—'}</b> · <span className="muted">{t('cdw.policyNumber')}: </span><b style={{ fontFamily: 'var(--font-mono)' }}>{s?.policy_number || '—'}</b></div>
        {s?.phone && <div style={row}><span className="muted">{t('set.phone')}: </span>{s.phone}{s?.homeowner_email ? <> · <span className="muted">Email: </span>{s.homeowner_email}</> : null}</div>}
        <div style={row}><span className="muted">{t('cdw.contractor')}: </span>{s?.contractor_name || '—'} · <span className="muted">{t('q.insurance')}: </span>{s?.insurance_company || '—'}</div>
        <div style={row}><span className="muted">{t('psum.policyType')}/{t('cci.language')}: </span>{s?.claim_type || s?.type_of_loss || '—'} · {s?.preferred_language || '—'} · <span className="muted">{t('cdw.dateOfLoss')}: </span>{s?.date_of_loss || '—'}</div>
        {s?.policy_summary?.deductible && <div style={row}><span className="muted">{t('psum.deductible')}: </span>{s.policy_summary.deductible}</div>}
        {/* HOQ / intake form (link traído de Monday): filear el claim sin abrir Monday. */}
        {s?.intake_form_url && (
          <a href={s.intake_form_url} target="_blank" rel="noopener noreferrer" className="btn ghost sm" style={{ justifySelf: 'start', marginTop: 2 }}>
            <ClipboardList size={14} strokeWidth={1.75} /> {t('cci.intakeForm')}
          </a>
        )}
      </div>
      {/* Casa + techo + medidas gratis de Google (como en Supplements). */}
      <PropertyRoofView claimId={claimId} />
    </div>
  );
}
